import { useReactiveVar } from '@apollo/client';
import React, { FC } from 'react';
import { vars } from '../../../../reactive';
import { Slot } from '../../../../reactive/main';
import { Slots } from '../../../../views/FormPages/Booking/DateAndTime';
import { ErrorMessage, Line } from '../../../Shared/Shared';
import { compareDatesWithoutTime, getUTCTime } from '../../helpers';
import { FORM_RADIO_COMPONENT_TYPES, FormDateAndTime } from '../../types';
import { BookingButton, FormTimeContainer, FormTimeLabel, FormTimeLabelContainer } from './styled';

interface FormRadioTimeProps {
  type: keyof typeof FORM_RADIO_COMPONENT_TYPES;
  formOptions: FormDateAndTime;
  sessionIndex: number;
  selectedSlot: Slot;
  orderGroupIndex: number;
  usedSchedules?: Slots['usedSchedules'];
  secondaryType: keyof typeof FORM_RADIO_COMPONENT_TYPES;
}

const FormRadioTime: FC<FormRadioTimeProps> = ({ type, formOptions, sessionIndex, selectedSlot, orderGroupIndex, usedSchedules, secondaryType }) => {
  const { registerAs, requiredMessage, errorMessage } = FORM_RADIO_COMPONENT_TYPES[type];
  const { registerAs: secondaryRegisterAs, requiredMessage: secondaryRequiredMessage, errorMessage: secondaryErrorMessage } = FORM_RADIO_COMPONENT_TYPES[secondaryType];

  const { register, formState } = formOptions;
  const product = useReactiveVar(vars.selectedProduct);
  const selectedOrderGroupSlots = useReactiveVar(vars.selectedOrderGroupSlots);
  const recurrenceType = useReactiveVar(vars.recurrenceType);
  const isRecurring = recurrenceType === 'recurring';
  const selectedSlots = selectedOrderGroupSlots[orderGroupIndex]?.slots || [];
  const slotsDate = isRecurring ? selectedSlot?.startDate : selectedSlot?.date;
  const getUsedSchedulesBySlot = (slotTime: string) => {
    return (
      usedSchedules?.map(schedule => {
        const slots = schedule.slots_availability?.availableSlots?.find(slot => compareDatesWithoutTime(new Date(slot[0]?.timestamp), slotsDate?.fullDate));
        const slot = slotsDate?.fullDate && slotTime ? slots?.find(slot => getUTCTime(slot) === slotTime) : undefined;
        return {
          slot,
          schedule
        };
      }) || []
    );
  };
  const usedSchedulesBySlot = getUsedSchedulesBySlot(selectedSlot?.time as string);

  const slots = slotsDate?.slots;
  const selectedProduct = useReactiveVar(vars.selectedProduct);
  const isBreakdownAllowed = !!product?.allow_availability_schedule_breakdown && usedSchedules?.every(schedule => !!schedule.BusUserId);

  const allSessionsFilled = selectedSlots.every(slot => slot?.time && (!isBreakdownAllowed || !!slot?.BusUserId)) && selectedSlots.length === selectedProduct?.sessions;

  const checkIfAvailable = (time: string): boolean => {
    const selectedSlotsInOtherGroups = selectedOrderGroupSlots?.filter((_, index) => index !== orderGroupIndex).flatMap(group => group.slots) || [];
    const selectedSltosInOtherSessions = selectedSlots.filter((_, index) => index !== sessionIndex);
    const selectedSlotsInDay = [...selectedSltosInOtherSessions, ...selectedSlotsInOtherGroups].filter(slot =>
      compareDatesWithoutTime(slot.date?.fullDate as Date, selectedSlot?.date?.fullDate as Date)
    );

    const selectedSlotsInDayTimes = selectedSlotsInDay.map(slot => slot.time);

    const alreadySelectedSlot = selectedSlotsInDayTimes.includes(time);
    const existingSlot = slots?.find(slot => getUTCTime(slot) === time);
    const isCheckoutOnly = existingSlot?.isCheckoutOnly;
    const isBreakdownValid = !isBreakdownAllowed || getUsedSchedulesBySlot(time).some(item => (item.slot?.available || 0) > 0);
    return (existingSlot?.available ?? 0) > 0 && !isCheckoutOnly && !alreadySelectedSlot && isBreakdownValid;
  };

  const slotsToShow = slots || [];

  if (slotsToShow.length === 1) {
    return null;
  }

  return (
    <>
      <FormTimeContainer>
        <FormTimeLabelContainer>
          {slotsToShow.map(slot => {
            const slotTime = getUTCTime(slot);
            return (
              <FormTimeLabel key={`${sessionIndex}_${slotTime}_${slot.timestamp}`}>
                <input
                  {...register(`${registerAs}.${sessionIndex}`, {
                    validate: (value: string) => {
                      if (!checkIfAvailable(value)) {
                        return errorMessage;
                      }

                      if (!allSessionsFilled) {
                        return requiredMessage;
                      }
                    }
                  })}
                  type="radio"
                  value={slotTime}
                  disabled={!checkIfAvailable(slotTime)}
                  checked={selectedSlot?.time === slotTime}
                />
                <BookingButton disabled={!checkIfAvailable(slotTime)}>
                  <span>{slotTime}</span>
                </BookingButton>
              </FormTimeLabel>
            );
          })}
          <Line />
          {isBreakdownAllowed &&
            usedSchedulesBySlot
              .filter(item => !!item.slot)
              ?.sort((a, b) => {
                const slotTime = getUTCTime(a?.slot!);
                const disabledA = !checkIfAvailable(slotTime) || (a?.slot?.available ?? 0) <= 0;
                const disabledB = !checkIfAvailable(slotTime) || (b?.slot?.available ?? 0) <= 0;
                if (disabledA && disabledB) {
                  return 0;
                }
                if (disabledA) {
                  return 1;
                }
                if (disabledB) {
                  return -1;
                }
                return Number(a?.slot?.available || 0) - Number(b?.slot?.available || 0);
              })
              .map(({ slot, schedule }) => {
                const slotTime = getUTCTime(slot!);
                const disabled = !checkIfAvailable(slotTime) || (slot?.available ?? 0) <= 0;
                return (
                  <FormTimeLabel key={`${sessionIndex}_${slotTime}_${schedule.id}`}>
                    <input
                      {...register(`${secondaryRegisterAs}.${sessionIndex}`, {
                        validate: (value: string) => {
                          if (!checkIfAvailable(slotTime)) {
                            return secondaryErrorMessage;
                          }
                          if (!allSessionsFilled) {
                            return secondaryRequiredMessage;
                          }
                        }
                      })}
                      type="radio"
                      value={schedule.BusUserId!}
                      disabled={disabled}
                      checked={selectedSlot?.BusUserId === schedule.BusUserId}
                    />
                    <BookingButton disabled={disabled}>
                      <span>
                        {schedule?.name} ({Math.max(Number(slot?.available || 0), 0)} / {slot?.mainSlotAvailablity})
                      </span>
                    </BookingButton>
                  </FormTimeLabel>
                );
              })}
        </FormTimeLabelContainer>
      </FormTimeContainer>

      {formState?.errors?.[registerAs] && <ErrorMessage>{formState.errors[registerAs].message || errorMessage}</ErrorMessage>}
      {formState?.errors?.[secondaryRegisterAs] && <ErrorMessage>{formState.errors[secondaryRegisterAs].message || secondaryErrorMessage}</ErrorMessage>}
    </>
  );
};

export default FormRadioTime;
